import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Badge } from '@/components/ui/badge';
import { 
  TrendingUp, 
  TrendingDown,
  Minus,
  AlertTriangle,
  CheckCircle,
  HelpCircle,
  ChevronDown,
  ChevronUp
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// Variance status configuration
const VARIANCE_CONFIG = {
  within_range: { 
    icon: CheckCircle, 
    color: 'text-green-600', 
    bgColor: 'bg-green-50', 
    borderColor: 'border-green-200',
    label: 'Within Range'
  },
  above_benchmark: { 
    icon: TrendingUp, 
    color: 'text-amber-600', 
    bgColor: 'bg-amber-50', 
    borderColor: 'border-amber-200',
    label: 'Above Benchmark'
  },
  below_benchmark: { 
    icon: TrendingDown, 
    color: 'text-blue-600', 
    bgColor: 'bg-blue-50', 
    borderColor: 'border-blue-200',
    label: 'Below Benchmark'
  },
  no_quote: { 
    icon: Minus, 
    color: 'text-slate-500', 
    bgColor: 'bg-slate-50', 
    borderColor: 'border-slate-200',
    label: 'No Quote Yet'
  }
};

export default function BenchmarkInsight({ jobId, job, className = "" }) {
  const [insight, setInsight] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  
  const token = localStorage.getItem('wyldecrest_token') || localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };
  
  useEffect(() => {
    loadInsight();
  }, [jobId]);
  
  const loadInsight = async () => {
    if (!jobId) return;
    
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/phs/jobs/${jobId}/benchmark-insight`, { headers });
      setInsight(res.data);
    } catch (err) {
      console.error('Failed to load benchmark insight:', err);
      setInsight(null);
    } finally {
      setLoading(false);
    }
  };
  
  if (loading) {
    return (
      <div className={`bg-white rounded-xl border border-slate-200 p-3 ${className}`}>
        <div className="animate-pulse flex items-center gap-2">
          <div className="w-8 h-8 bg-slate-200 rounded-lg" />
          <div className="flex-1">
            <div className="h-3 bg-slate-200 rounded w-24 mb-1" />
            <div className="h-4 bg-slate-200 rounded w-32" />
          </div>
        </div>
      </div>
    );
  }
  
  if (!insight || !insight.has_benchmark) {
    return (
      <div className={`bg-slate-50 rounded-xl border border-slate-200 p-3 ${className}`} data-testid="benchmark-insight-empty">
        <div className="flex items-center gap-2 text-slate-400">
          <HelpCircle className="h-4 w-4" />
          <span className="text-xs">No benchmark data for this job type</span>
        </div>
      </div>
    );
  }
  
  const { benchmark, quote_value, variance_percent, variance_status } = insight;
  const config = VARIANCE_CONFIG[variance_status] || VARIANCE_CONFIG.no_quote;
  const StatusIcon = config.icon;
  
  // Format currency
  const formatCurrency = (value) => {
    if (value === null || value === undefined) return '-';
    return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', minimumFractionDigits: 0 }).format(value);
  };
  
  return (
    <div 
      className={`${config.bgColor} rounded-xl border ${config.borderColor} overflow-hidden ${className}`}
      data-testid="benchmark-insight"
    >
      {/* Header */}
      <div className="p-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className={`w-8 h-8 rounded-lg ${config.bgColor} border ${config.borderColor} flex items-center justify-center`}>
              <TrendingUp className={`h-4 w-4 ${config.color}`} />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wide text-slate-500 font-medium">Benchmark Insight</div>
              <div className="text-sm font-semibold text-slate-700">{benchmark.job_type}</div>
            </div>
          </div>
          <button
            onClick={() => setExpanded(!expanded)}
            className="p-1 hover:bg-white/50 rounded transition"
          >
            {expanded ? (
              <ChevronUp className="h-4 w-4 text-slate-400" />
            ) : (
              <ChevronDown className="h-4 w-4 text-slate-400" />
            )}
          </button>
        </div>
        
        {/* Summary Stats */}
        <div className="mt-3 grid grid-cols-3 gap-2">
          <div className="text-center p-2 bg-white/50 rounded-lg">
            <div className="text-[10px] text-slate-500 uppercase">Range</div>
            <div className="text-xs font-semibold text-slate-700">
              {formatCurrency(benchmark.typical_low)} - {formatCurrency(benchmark.typical_high)}
            </div>
          </div>
          <div className="text-center p-2 bg-white/50 rounded-lg">
            <div className="text-[10px] text-slate-500 uppercase">Average</div>
            <div className="text-xs font-semibold text-slate-700">
              {formatCurrency(benchmark.average_cost)}
            </div>
          </div>
          <div className="text-center p-2 bg-white/50 rounded-lg">
            <div className="text-[10px] text-slate-500 uppercase">Quote</div>
            <div className={`text-xs font-semibold ${quote_value > 0 ? config.color : 'text-slate-400'}`}>
              {quote_value > 0 ? formatCurrency(quote_value) : 'Pending'}
            </div>
          </div>
        </div>
        
        {/* Variance Badge */}
        {variance_status !== 'no_quote' && (
          <div className="mt-3 flex items-center justify-between">
            <Badge className={`${config.bgColor} ${config.color} border ${config.borderColor} gap-1`}>
              <StatusIcon className="h-3 w-3" />
              {config.label}
            </Badge>
            {variance_percent !== null && (
              <span className={`text-xs font-semibold ${config.color}`}>
                {variance_percent > 0 ? '+' : ''}{variance_percent.toFixed(1)}%
              </span>
            )}
          </div>
        )}
      </div>
      
      {/* Expanded Details */}
      {expanded && (
        <div className="border-t border-white/50 p-3 bg-white/30">
          {/* Common Scope Items */}
          {benchmark.common_scope_items?.length > 0 && (
            <div className="mb-3">
              <div className="text-[10px] uppercase tracking-wide text-slate-500 font-medium mb-1">
                Typical Scope Items
              </div>
              <div className="flex flex-wrap gap-1">
                {benchmark.common_scope_items.map((item, idx) => (
                  <Badge key={idx} variant="outline" className="text-[10px] bg-white">
                    {item}
                  </Badge>
                ))}
              </div>
            </div>
          )}
          
          {/* Common Variations */}
          {benchmark.common_variations?.length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-wide text-slate-500 font-medium mb-1">
                Common Variations
              </div>
              <div className="flex flex-wrap gap-1">
                {benchmark.common_variations.map((item, idx) => (
                  <Badge key={idx} variant="outline" className="text-[10px] bg-amber-50 text-amber-700 border-amber-200">
                    {item}
                  </Badge>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
